/**
 * StandardText Create Page
 * Create new standard text
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, Alert } from '@mui/material';
import { PageShell } from '../../components';
import { standardTextService } from '../../services/standardTextService';
import { StandardText, StandardTextCategory, StandardTextFormData } from '../../types/standardText';
import { StandardTextForm } from './components/StandardTextForm';

const getApiErrorMessage = (err: unknown, fallback: string): string => {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const { response } = err as { response?: { data?: { error?: string } } };
    if (response?.data?.error) return response.data.error;
  }
  return err instanceof Error ? err.message : fallback;
};

const ehCategoria = (valor: string | null): valor is StandardTextCategory =>
  valor !== null && (Object.values(StandardTextCategory) as string[]).includes(valor);

export const StandardTextCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A recusa do salvar mora ao lado do conteúdo, e não no alerta do topo:
  // é lá que está o texto que ela manda corrigir.
  const [erroDeEnvio, setErroDeEnvio] = useState<string | null>(null);
  const [categories, setCategories] = useState<Array<{ value: string; label: string }>>([]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await standardTextService.getCategories();
        setCategories(cats);
      } catch (err: unknown) {
        console.error('Erro ao carregar categorias:', err);
        setError('Erro ao carregar categorias');
      }
    };
    loadCategories();
  }, []);

  // Destino do link das pendências `SECAO_SEM_TEXTO` e `SECAO_SEM_TEXTO_PROPRIO`
  // da emissão: sem chegar preenchido, o link deixa a pessoa adivinhando de que
  // seção o documento estava falando.
  const initialData = useMemo<Partial<StandardText> | undefined>(() => {
    const categoria = searchParams.get('category');
    const secao = searchParams.get('sectionKey');
    if (!ehCategoria(categoria)) return undefined;

    return { category: categoria, sectionKey: secao };
  }, [searchParams]);

  const handleSubmit = async (data: StandardTextFormData) => {
    try {
      setLoading(true);
      setErroDeEnvio(null);
      await standardTextService.createStandardText(data);
      navigate('/standard-texts');
    } catch (err: unknown) {
      setErroDeEnvio(getApiErrorMessage(err, 'Erro ao criar texto padrão'));
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/standard-texts');
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Textos Padronizados', path: '/standard-texts' },
        { label: 'Cadastrar' },
      ]}
      title="Cadastrar Novo Texto Padronizado"
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <StandardTextForm
            initialData={initialData}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={loading}
            submitError={erroDeEnvio}
            categories={categories}
          />
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default StandardTextCreatePage;
